import React, { useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, ChevronDown, ChevronRight, Loader2, ScanSearch, SearchCheck } from 'lucide-react';
import type { GbuHinweis, GbuKiPruefHinweis, GbuKiVorschlagGefaehrdung, GbuVoll, Kataster } from '../../../services/sifa/gbuTypes';
import { ohneDubletten, pruefeGbuMitKi } from '../../../services/sifa/gbuKi';
import { aktiveGefaehrdungen } from '../../../services/sifa/gbuLogic';
import { faktorLabel } from '../../../services/sifa/kataloge/gefaehrdungsfaktoren';
import { psaLabel } from '../../../services/sifa/kataloge/psaKatalog';
import { anlassLabel } from '../../../services/sifa/kataloge/arbmedvvAnlaesse';
import { RisikoBadge } from './RisikoWahl';
import { HINWEIS_TON, MASSNAHME_ART_TON, sortiereHinweise, vorschlagAusEinzel } from './gbuModulLogic';

interface Props {
  voll: GbuVoll;
  kataster: Kataster;
  branche: string | null;
  /** Feste Regeln (gbuLogic.pruefeGbu) — laufen immer, ohne KI. */
  hinweise: GbuHinweis[];
  /** KI-Pruefung und Uebernahme nur im bearbeitbaren Entwurf. */
  kiErlaubt: boolean;
  onSpringeZu?: (gefaehrdungId: string) => void;
  /** Vorschlag eines KI-Hinweises im Pruefbildschirm (GbuKiDialog) oeffnen — dort angehakt, dann uebernommen. */
  onVorschlagPruefen: (vorschlag: GbuKiVorschlagGefaehrdung) => void;
}

const ART_LABEL: Record<GbuKiPruefHinweis['art'], string> = {
  luecke: 'Lücke',
  widerspruch: 'Widerspruch',
  verbesserung: 'Verbesserung',
};

const STUFE_LABEL: Record<GbuHinweis['stufe'], string> = {
  sperre: 'Verhindert die Freigabe',
  warnung: 'Warnung',
  hinweis: 'Hinweis',
};

/**
 * Pruefhinweise: die festen Regeln immer sichtbar (Sperren verhindern „Zur
 * Freigabe", Warnungen nicht — O4), darunter auf Knopfdruck die
 * KI-Vollstaendigkeitspruefung (K2).
 *
 * Ein KI-Hinweis mit fertigem Vorschlag wird NIE ungesehen geschrieben: der
 * Vorschlag laesst sich hier aufklappen (Massnahmen, Vorsorge, W/S) und geht
 * zum Uebernehmen ueber den Pruefbildschirm mit Haken je Zeile.
 */
export const GbuPruefHinweise: React.FC<Props> = ({ voll, kataster, branche, hinweise, kiErlaubt, onSpringeZu, onVorschlagPruefen }) => {
  const [kiLaeuft, setKiLaeuft] = useState(false);
  const [kiHinweise, setKiHinweise] = useState<GbuKiPruefHinweis[] | null>(null);
  const [kiFehler, setKiFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState<ReadonlySet<number>>(() => new Set());

  const sortiert = sortiereHinweise(hinweise);
  const aktive = aktiveGefaehrdungen(voll);
  /** Schon erfasst (Textabgleich wie beim KI-Vorschlag) — dann nichts mehr anbieten. */
  const schonErfasst = (v: GbuKiVorschlagGefaehrdung): boolean => ohneDubletten(vorschlagAusEinzel(v), aktive).gefaehrdungen.length === 0;

  const kiPruefen = async () => {
    if (kiLaeuft) return;
    setKiLaeuft(true);
    setKiFehler(null);
    try {
      const erg = await pruefeGbuMitKi(voll, kataster, branche);
      if (erg.status === 'ok') {
        setKiHinweise(erg.hinweise);
        setOffen(new Set());
      } else {
        // Ein Ausfall heisst NICHT „vollstaendig" — die Liste bleibt leer, der Grund steht da.
        setKiHinweise(null);
        setKiFehler(`KI-Prüfung nicht möglich: ${erg.text}`);
      }
    } finally {
      setKiLaeuft(false);
    }
  };

  const umschalten = (i: number) => setOffen((s) => {
    const n = new Set(s);
    if (n.has(i)) n.delete(i); else n.add(i);
    return n;
  });

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-bold text-slate-800">Prüfhinweise</p>
        {kiErlaubt && (
          <button onClick={() => void kiPruefen()} disabled={kiLaeuft} className="min-h-[40px] px-3 flex items-center gap-1.5 text-xs font-bold text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-lg disabled:opacity-40">
            {kiLaeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanSearch className="w-3.5 h-3.5" />} KI-Prüfung
          </button>
        )}
      </div>

      {sortiert.length === 0 ? (
        <p className="text-xs text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Die festen Prüfregeln finden nichts.</p>
      ) : (
        <ul className="space-y-1.5">
          {sortiert.map((h, i) => (
            <li key={`${h.code}-${h.gefaehrdungId ?? ''}-${h.massnahmeId ?? ''}-${i}`} className={`px-3 py-2 text-xs border rounded-lg flex items-start gap-2 ${HINWEIS_TON[h.stufe]}`}>
              {h.stufe !== 'hinweis' && <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />}
              <span className="flex-1 min-w-0">
                <b>{STUFE_LABEL[h.stufe]}:</b> {h.text}
              </span>
              {h.gefaehrdungId && onSpringeZu && (
                <button onClick={() => onSpringeZu(h.gefaehrdungId as string)} className="shrink-0 text-[11px] font-bold underline">Zur Gefährdung</button>
              )}
            </li>
          ))}
        </ul>
      )}

      {kiFehler && (
        <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" /> <span>{kiFehler}</span>
        </div>
      )}

      {kiHinweise && (
        <div className="space-y-1.5 border-t border-slate-100 pt-3">
          <p className="text-[11px] font-black uppercase tracking-wide text-violet-700">KI-Prüfung</p>
          {kiHinweise.length === 0 ? (
            <p className="text-xs text-slate-600">Die KI hat keine Lücke gefunden. Das ersetzt die fachliche Prüfung nicht.</p>
          ) : (
            kiHinweise.map((h, i) => {
              const v = h.vorschlag;
              const erfasst = v ? schonErfasst(v) : false;
              return (
                <div key={i} className="px-3 py-2 text-xs border border-violet-200 bg-violet-50/50 rounded-lg space-y-1.5">
                  <p className="text-slate-700">
                    <b>{ART_LABEL[h.art]}{h.faktor ? ` · ${faktorLabel(h.faktor)}` : ''}:</b> {h.text}
                  </p>
                  {v && (
                    <>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <button onClick={() => umschalten(i)} className="inline-flex items-center gap-1 text-slate-600 font-semibold hover:underline">
                          {offen.has(i) ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          Vorschlag: {v.gefaehrdung} ({v.massnahmen.length} Maßnahme(n))
                        </button>
                        {erfasst ? (
                          <span className="inline-flex items-center gap-1 font-bold text-emerald-700"><Check className="w-3.5 h-3.5" /> schon erfasst</span>
                        ) : kiErlaubt ? (
                          <button onClick={() => onVorschlagPruefen(v)} className="min-h-[36px] px-3 flex items-center gap-1 font-bold text-white bg-violet-600 hover:bg-violet-700 rounded-lg">
                            <SearchCheck className="w-3.5 h-3.5" /> Prüfen und übernehmen
                          </button>
                        ) : null}
                      </div>
                      {offen.has(i) && (
                        <div className="pl-5 space-y-1 text-slate-700">
                          <p className="flex items-center gap-2">
                            <span className="text-slate-500">Risiko</span>
                            <RisikoBadge w={v.wVor} s={v.sVor} titel="vorher" /> <span className="text-slate-400">→</span> <RisikoBadge w={v.wNach} s={v.sNach} titel="nachher" />
                          </p>
                          {v.folgen && <p><span className="text-slate-500">Folgen:</span> {v.folgen}</p>}
                          {v.massnahmen.map((m) => (
                            <p key={m.tempId} className="flex items-start gap-1.5">
                              <span className={`shrink-0 px-1.5 font-black rounded ${MASSNAHME_ART_TON[m.art]}`}>{m.art}</span>
                              <span>{m.text}{m.psaCode ? ` — ${psaLabel(m.psaCode)}` : ''}</span>
                            </p>
                          ))}
                          {v.vorsorge.map((x) => <p key={x.tempId}><span className="text-slate-500">Vorsorge:</span> {anlassLabel(x.anlassCode)}</p>)}
                          {v.vorschriften.length > 0 && <p className="text-slate-500">{v.vorschriften.join(' · ')}</p>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
